import { parseAdministratorAuthenticationConfiguration } from "@reminder-tracker/auth";
import { ConvexProviderWithAuth, ConvexReactClient, useConvexAuth } from "convex/react";
import { useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { AdministratorAuthenticationProvider } from "./authentication-context";
import { SessionBridge } from "./session-bridge";
import { localAdministratorBoundary } from "./auth-session";
import {
  createBrowserSessionBroadcast,
  createFetchWebSessionApi,
  createWebSessionController,
  type WebSessionController,
} from "./web-session-client";

function DisconnectedProviders({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <AdministratorAuthenticationProvider
      value={{ authentication: localAdministratorBoundary, signIn: null, signOut: null }}
    >
      {children}
    </AdministratorAuthenticationProvider>
  );
}

function ConnectedBridge(
  props: Readonly<{
    children: ReactNode;
    controller: WebSessionController;
    expectedOrganizationId: string;
  }>,
) {
  const convex = useConvexAuth();
  return <SessionBridge {...props} convexAuthenticated={convex.isAuthenticated} />;
}

function ConnectedProviders({
  children,
  convexUrl,
  administratorOrganizationId,
}: Readonly<{
  children: ReactNode;
  clientId: string;
  convexUrl: string;
  administratorOrganizationId: string;
}>) {
  const controller = useMemo(
    () =>
      createWebSessionController({
        api: createFetchWebSessionApi(),
        broadcast: createBrowserSessionBroadcast(),
        nowEpochSeconds: () => Math.floor(Date.now() / 1000),
        schedule: (callback, delay) => globalThis.setTimeout(callback, delay),
        cancelSchedule: (handle) => globalThis.clearTimeout(handle as number),
      }),
    [],
  );
  const lifecycle = useRef(0);
  useEffect(() => {
    const generation = ++lifecycle.current;
    void controller.initialize();
    const visible = () => {
      if (document.visibilityState === "visible") void controller.onVisible();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      queueMicrotask(() => {
        if (lifecycle.current === generation) {
          controller.dispose();
        }
      });
    };
  }, [controller]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const connectionKey =
    snapshot.state === "authenticated"
      ? JSON.stringify([snapshot.user.id, snapshot.organizationId])
      : snapshot.state;
  return (
    <AdministratorConnection
      key={connectionKey}
      controller={controller}
      convexUrl={convexUrl}
      administratorOrganizationId={administratorOrganizationId}
    >
      {children}
    </AdministratorConnection>
  );
}

function AdministratorConnection({
  children,
  controller,
  convexUrl,
  administratorOrganizationId,
}: Readonly<{
  children: ReactNode;
  controller: WebSessionController;
  convexUrl: string;
  administratorOrganizationId: string;
}>) {
  const convex = useMemo(() => new ConvexReactClient(convexUrl), [convexUrl]);
  const lifecycle = useRef(0);
  useEffect(() => {
    const generation = ++lifecycle.current;
    return () =>
      queueMicrotask(() => {
        if (lifecycle.current === generation) void convex.close();
      });
  }, [convex]);
  const useSessionAuth = useMemo(
    () =>
      function useSessionAuth() {
        const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
        return {
          isLoading: snapshot.state === "initializing",
          isAuthenticated: snapshot.state === "authenticated",
          fetchAccessToken: controller.fetchAccessToken,
        };
      },
    [controller],
  );
  return (
    <ConvexProviderWithAuth client={convex} useAuth={useSessionAuth}>
      <ConnectedBridge controller={controller} expectedOrganizationId={administratorOrganizationId}>
        {children}
      </ConnectedBridge>
    </ConvexProviderWithAuth>
  );
}

export function RuntimeProviders({
  children,
}: Readonly<{ children: ReactNode }>): React.JSX.Element {
  const publicEnvironment = import.meta.env as Readonly<Record<string, unknown>>;
  const optionalString = (value: unknown): string | undefined =>
    typeof value === "string" ? value : undefined;
  const configuration = parseAdministratorAuthenticationConfiguration({
    environment: optionalString(publicEnvironment["VITE_RT_ENVIRONMENT"]),
    workOsClientId: optionalString(publicEnvironment["VITE_WORKOS_CLIENT_ID"]),
    convexUrl: optionalString(publicEnvironment["VITE_CONVEX_URL"]),
    administratorOrganizationId: optionalString(
      publicEnvironment["VITE_WORKOS_ADMIN_ORGANIZATION_ID"],
    ),
  });
  if (configuration.state === "disconnected") {
    return <DisconnectedProviders>{children}</DisconnectedProviders>;
  }
  return (
    <ConnectedProviders
      clientId={configuration.workOsClientId}
      convexUrl={configuration.convexUrl}
      administratorOrganizationId={configuration.administratorOrganizationId}
    >
      {children}
    </ConnectedProviders>
  );
}
